import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowUpRight, Brain, Activity } from "lucide-react";

const IMG =
  "https://images.unsplash.com/photo-1758216434916-0819f9cd3a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHdhdGVyY29sb3IlMjBuZXV0cmFsJTIwY2FsbXxlbnwwfHx8fDE3NzczNDE5MDl8MA&ixlib=rb-4.1.0&q=85";

const brainSymptoms = [
  "Brain fog",
  "Dizziness",
  "Nausea",
  "Headaches",
];

const bodySymptoms = [
  "Non-epileptic seizures",
  "Temporary loss of vision or hearing",
  "Temporary paralysis",
  "A body that feels heavy",
  "Tics & involuntary movements",
];

const faqs = [
  {
    q: "Is FND \"all in my head\"?",
    a: "No. FND is a neurological disorder — the wiring is intact, but the messages your brain sends to your body are scrambled. The experience is physical, measurable in its effects, and not a choice.",
    testid: "faq-all-in-head",
  },
  {
    q: "Why doesn't it show up on a brain scan?",
    a: "Other neurological conditions often show physical damage on imaging. FND is different — it's a problem of function, not structure. You can think of it as a software glitch rather than broken hardware.",
    testid: "faq-scan",
  },
  {
    q: "If I can briefly stop a symptom, does that mean I'm faking?",
    a: "No. Many people with FND can, for short windows, force symptoms down — but doing so usually makes things worse later. That fluctuation is part of the disorder, not proof against it.",
    testid: "faq-faking",
  },
  {
    q: "Will two people with FND look the same?",
    a: "Almost never. FND is deeply individual — one person's symptoms, triggers, and rhythms can look nothing like another's. Your experience is valid even when it doesn't match someone else's.",
    testid: "faq-same",
  },
  {
    q: "Can FND get better?",
    a: "Many people do find their symptoms ease or settle over time, especially with understanding, supportive care, and patient pacing. There's no single path — only the one that fits you.",
    testid: "faq-better",
  },
];

export default function WhatIsFND() {
  return (
    <div data-testid="page-what-is-fnd">
      {/* Header */}
      <section className="pt-16 lg:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
            <span className="w-6 h-px bg-terracotta" />
            Chapter One
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h1
              data-testid="what-title"
              className="lg:col-span-8 font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight text-balance"
            >
              What is
              <span className="italic text-terracotta"> Functional Neurological </span>
              Disorder?
            </h1>
            <p className="lg:col-span-4 text-lg text-charcoal-light dark:text-bone/65 leading-relaxed">
              A neurological disorder in which your brain sometimes fails to
              communicate properly with your body. Simple to say — harder to
              live inside of.
            </p>
          </div>
        </div>
      </section>

      {/* Definition split */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[2rem]">
                <img
                  src={IMG}
                  alt="Soft flowing fabric"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss">
                <span className="w-6 h-px bg-moss" />
                The simple definition
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal dark:text-bone leading-[1.08] tracking-tight text-balance">
                The wiring is fine. The <em className="text-terracotta">messages</em> aren’t.
              </h2>
              <p className="text-lg text-charcoal-light dark:text-bone/65 leading-relaxed">
                FND is a neurological disorder. That means the brain itself
                is involved — but not in the way most people expect. The
                structure of the brain isn’t damaged. Instead, the
                <em> way </em> signals are sent between brain and body gets
                disrupted.
              </p>
              <p className="text-lg text-charcoal-light dark:text-bone/65 leading-relaxed">
                A signal to move ends up as stillness. A signal to see ends up
                as blur. The body does what it’s told — it’s just being told
                the wrong thing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common symptoms — Brain + Body */}
      <section className="py-20 lg:py-28 bg-bone-100/60 dark:bg-charcoal-light/10" data-testid="common-symptoms">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
              <span className="w-6 h-px bg-terracotta" />
              Common symptoms
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal dark:text-bone leading-[1.08] tracking-tight text-balance">
              Sorted into <em className="text-terracotta">brain</em> and <em className="text-terracotta">body</em>.
            </h2>
            <p className="mt-5 text-base text-charcoal-light dark:text-bone/65 leading-relaxed max-w-xl">
              A snapshot here — the Symptoms page goes deeper with an
              interactive explorer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              data-testid="symptom-group-brain"
              className="rounded-3xl bg-bone dark:bg-charcoal-light/10 border border-bone-200 dark:border-charcoal-light/25 p-8 lg:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-moss/15 text-moss">
                  <Brain size={20} strokeWidth={1.5} />
                </span>
                <h3 className="font-serif text-3xl text-charcoal dark:text-bone tracking-tight">Brain</h3>
              </div>
              <ul className="space-y-3">
                {brainSymptoms.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-3 text-base text-charcoal"
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-moss shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              data-testid="symptom-group-body"
              className="rounded-3xl bg-bone dark:bg-charcoal-light/10 border border-bone-200 dark:border-charcoal-light/25 p-8 lg:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-terracotta/15 text-terracotta">
                  <Activity size={20} strokeWidth={1.5} />
                </span>
                <h3 className="font-serif text-3xl text-charcoal dark:text-bone tracking-tight">Body</h3>
              </div>
              <ul className="space-y-3">
                {bodySymptoms.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-3 text-base text-charcoal"
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How FND differs */}
      <section className="py-20 lg:py-28" data-testid="differs-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
            <span className="w-6 h-px bg-moss" />
            How FND differs
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal dark:text-bone leading-[1.08] tracking-tight text-balance">
            Invisible on a scan — but <em className="text-terracotta">present</em> in every hour of the day.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone-100/50 dark:bg-charcoal-light/10 p-8">
              <p className="text-xs uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/65 mb-3">
                Other neurological conditions
              </p>
              <p className="text-charcoal dark:text-bone text-lg leading-relaxed">
                Often show <em>physical</em> damage — lesions, shrinkage,
                plaques — visible on an MRI or CT.
              </p>
            </div>
            <div className="rounded-3xl border border-terracotta/30 bg-terracotta/10 p-8">
              <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                FND
              </p>
              <p className="text-charcoal dark:text-bone text-lg leading-relaxed">
                Shows <em>nothing visible</em>. The hardware looks fine. It’s
                how the brain is using itself that’s gone awry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Misconceptions & Stigma */}
      <section className="py-20 lg:py-28 bg-charcoal text-bone" data-testid="stigma-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
            <span className="w-6 h-px bg-terracotta" />
            Misconceptions & stigma
          </span>
          <h2 className="font-serif text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
            “It’s all in your head” is the <em className="text-terracotta">most corrosive </em>
            thing someone with FND can hear.
          </h2>
          <div className="mt-10 space-y-5 text-lg text-bone/80 leading-relaxed max-w-3xl">
            <p>
              A common misconception is that FND isn’t real — that it’s
              being imagined, exaggerated, or performed. This is partly
              because people with FND can sometimes force a symptom to stop
              for a brief window of time.
            </p>
            <p>
              But that window comes at a cost: the symptoms almost always
              rebound harder afterwards. A moment of control is not the
              same as a cure, and it is certainly not evidence that the
              condition was ever fake.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
              <span className="w-6 h-px bg-terracotta" />
              Questions, softly answered
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal dark:text-bone leading-[1.08] tracking-tight text-balance">
              The things people ask <em className="text-terracotta">most often</em>.
            </h2>
          </div>

          <Accordion
            type="single"
            collapsible
            className="w-full"
            data-testid="faq-accordion"
          >
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.testid}
                value={`faq-${i}`}
                className="border-b border-bone-200 dark:border-charcoal-light/25 py-2"
              >
                <AccordionTrigger
                  data-testid={f.testid}
                  className="text-left font-serif text-xl sm:text-2xl text-charcoal dark:text-bone hover:no-underline hover:text-terracotta py-5 [&[data-state=open]]:text-terracotta"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-charcoal-light dark:text-bone/65 leading-relaxed pb-6 pr-8">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA to symptoms */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/symptoms"
            data-testid="what-cta-symptoms"
            className="group relative block overflow-hidden rounded-[2rem] bg-terracotta text-bone p-10 lg:p-14 hover-lift"
          >
            <span className="text-xs uppercase tracking-[0.28em] text-bone/70">Next</span>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Explore symptoms & types, at your own pace.
            </h3>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b border-bone/50 pb-1 group-hover:border-bone transition">
              Open the explorer
              <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
            <div className="pointer-events-none absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-bone/20 blur-3xl" />
          </Link>
        </div>
      </section>
    </div>
  );
}
